<template>
  <div>
    <v-container fluid class="pa-4">
      <!-- Hero Header -->
      <v-card class="hero-header mb-4" elevation="0">
        <div class="hero-content">
          <div class="hero-icon-wrapper">
            <v-icon size="44" color="white">mdi-tent</v-icon>
          </div>
          <div class="hero-text">
            <h1 class="hero-title">กำหนดการปรับข้อมูลส่วนตัว ด้านวุฒิลูกเสือ</h1>
            <p class="hero-subtitle">
              ควบคุมการเปิด/ปิดให้บุคลากรปรับข้อมูลด้านวุฒิลูกเสือของตนเอง
              (หน้า "ข้อมูลลูกเสือวิสามัญอาชีวศึกษา" ที่บุคคลเข้าใช้งานเอง) —
              ไม่มีผลกับการที่งานบุคลากรวิทยาลัยแก้ไขแทน ซึ่งทำได้ตลอดปี
            </p>
          </div>
          <v-spacer></v-spacer>
          <v-btn class="edit-btn" large depressed :loading="loading" @click="periodEdit()">
            <v-icon left>mdi-pencil</v-icon>
            แก้ไขกำหนดการ
          </v-btn>
        </div>
      </v-card>

      <v-row>
        <v-col cols="12" md="4">
          <v-card class="status-card" outlined :class="isOpen ? 'status-open' : 'status-closed'">
            <v-card-text class="d-flex align-center">
              <v-icon size="36" :color="isOpen ? 'success' : 'error'" class="mr-3">
                {{ isOpen ? "mdi-lock-open-check-outline" : "mdi-lock-outline" }}
              </v-icon>
              <div>
                <div class="status-label">สถานะระบบ</div>
                <div class="status-value" :class="isOpen ? 'success--text' : 'error--text'">
                  {{ isOpen ? "เปิดใช้งาน" : "ปิดใช้งาน" }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="status-card" outlined>
            <v-card-text class="d-flex align-center">
              <v-icon size="36" color="primary" class="mr-3">mdi-calendar-range</v-icon>
              <div>
                <div class="status-label">ช่วงเวลาที่เปิด</div>
                <div class="status-value-sm">
                  {{ periods.period_start | moment("add", "543 years") | moment("D MMM YYYY") }}
                  -
                  {{ periods.period_stop | moment("add", "543 years") | moment("D MMM YYYY") }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="status-card" outlined>
            <v-card-text class="d-flex align-center">
              <v-icon size="36" color="warning darken-2" class="mr-3">mdi-timer-sand</v-icon>
              <div>
                <div class="status-label">เหลือเวลา</div>
                <div class="status-value-sm">{{ daysLeftLabel }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card class="elevation-1 mt-2">
        <v-card-text>
          <v-simple-table class="modern-table">
            <tbody>
              <tr>
                <td class="font-weight-medium grey--text text--darken-2">ครั้งที่</td>
                <td class="text-center font-weight-bold primary--text">{{ periods.period_times }}</td>
                <td class="font-weight-medium grey--text text--darken-2">ปีการศึกษา</td>
                <td class="text-center font-weight-bold primary--text">{{ periods.period_year }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium grey--text text--darken-2">เริ่มวันที่</td>
                <td class="text-center font-weight-bold">
                  {{ periods.period_start | moment("add", "543 years") | moment("D MMMM YYYY") }}
                </td>
                <td class="font-weight-medium grey--text text--darken-2">สิ้นสุดวันที่</td>
                <td class="text-center font-weight-bold">
                  {{ periods.period_stop | moment("add", "543 years") | moment("D MMMM YYYY") }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Edit Dialog -->
    <v-dialog v-model="editperioddialog" persistent max-width="800px">
      <v-card>
        <v-card-title class="edit-dialog-header white--text">
          <v-icon left color="white">mdi-tent</v-icon>
          แก้ไขกำหนดการปรับข้อมูลส่วนตัว ด้านวุฒิลูกเสือ
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="editperiodform" v-model="valid" lazy-validation>
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editperiod.period_year"
                  :items="periodselect"
                  item-text="text"
                  item-value="value"
                  label="เลือกปีการศึกษา"
                  outlined
                  dense
                  :rules="[v => !!v || 'กรุณาเลือกปีการศึกษา']"
                  prepend-inner-icon="mdi-calendar"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editperiod.period_times"
                  type="number"
                  label="ครั้งที่"
                  outlined
                  dense
                  :rules="[v => !!v || 'กรุณาระบุครั้งที่']"
                  prepend-inner-icon="mdi-numeric"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editperiod.period_start"
                  type="date"
                  label="วันที่เริ่มต้น"
                  outlined
                  dense
                  :rules="[v => !!v || 'กรุณาเลือกวันที่เริ่มต้น']"
                  prepend-inner-icon="mdi-calendar-start"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editperiod.period_stop"
                  type="date"
                  label="วันที่สิ้นสุด"
                  outlined
                  dense
                  :rules="[v => !!v || 'กรุณาเลือกวันที่สิ้นสุด']"
                  prepend-inner-icon="mdi-calendar-end"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-card outlined class="pa-3">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="font-weight-bold">เปิดให้ปรับข้อมูลส่วนตัวด้านวุฒิลูกเสือ</div>
                      <div class="text-caption grey--text">
                        เมื่อปิด บุคลากรจะเห็นข้อมูลเดิมได้ แต่จะเพิ่ม/แก้ไข/ลบเองไม่ได้จนกว่าจะเปิดรอบถัดไป
                      </div>
                    </div>
                    <v-switch v-model="editperiod.period_enable_sw" color="success" inset></v-switch>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn large outlined @click="editperioddialog = false" class="px-6">
            <v-icon left>mdi-close</v-icon>ยกเลิก
          </v-btn>
          <v-btn large color="success" dark @click="editperiodSubmit()" class="px-6">
            <v-icon left>mdi-content-save</v-icon>บันทึกการแก้ไข
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      multi-line
      vertical
      top
      auto-height
      :timeout="snackbar.timeout"
      :color="snackbar.color"
    >
      <v-icon large>{{ snackbar.icon }}</v-icon>
      <br />
      {{ snackbar.text }}
      <v-btn dark @click="snackbar.show = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>
<script>
import VueMoment from "vue-moment";
import moment from "moment-timezone";
export default {
  name: "period_scout",
  data() {
    return {
      VueMoment,
      moment,
      loading: true,
      valid: true,
      ApiKey: "HRvec2021",
      editperioddialog: false,
      snackbar: { show: false, color: "", timeout: 5000, icon: "", text: "" },
      periods: {},
      editperiod: {},
      periodselect: [
        { value: "2564", text: "2564" },
        { value: "2565", text: "2565" },
        { value: "2566", text: "2566" },
        { value: "2567", text: "2567" },
        { value: "2568", text: "2568" },
        { value: "2569", text: "2569" },
        { value: "2570", text: "2570" },
        { value: "2571", text: "2571" },
        { value: "2572", text: "2572" }
      ],
      data_syslog: {}
    };
  },
  computed: {
    isOpen() {
      return this.periods.period_enable === "1" || this.periods.period_enable === 1;
    },
    daysLeftLabel() {
      if (!this.isOpen || !this.periods.period_stop) return "-";
      const today = new Date(new Date().toDateString());
      const stop = new Date(this.periods.period_stop);
      const days = Math.ceil((stop - today) / (24 * 60 * 60 * 1000));
      if (days < 0) return "ปิดรับแล้ว";
      if (days === 0) return "ปิดวันนี้";
      return `เหลืออีก ${days} วัน`;
    },
    date_today_log() {
      let today = new Date();
      let dd = String(today.getDate()).padStart(2, "0");
      let mm = String(today.getMonth() + 1).padStart(2, "0");
      let yyyy = today.getFullYear() + 543;
      let time =
        today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds();
      today = dd + "/" + mm + "/" + yyyy + "/" + time;
      return today;
    }
  },
  async mounted() {
    await this.periodQueryAll();
  },
  methods: {
    async periodQueryAll() {
      this.loading = true;
      try {
        let result = await this.$http.post("period.php", {
          ApiKey: this.ApiKey,
          period_id: "501"
        });
        this.periods = result.data;
      } finally {
        this.loading = false;
      }
    },
    async periodEdit() {
      let result = await this.$http.post("period.php", {
        ApiKey: this.ApiKey,
        period_id: "501"
      });
      this.editperiod = result.data;
      this.editperiod.period_enable_sw =
        this.editperiod.period_enable === "1" || this.editperiod.period_enable === 1;
      this.editperioddialog = true;
    },
    async editperiodSubmit() {
      if (!this.$refs.editperiodform.validate()) return;
      this.editperiod.period_enable = this.editperiod.period_enable_sw ? "1" : "0";
      this.editperiod.ApiKey = this.ApiKey;
      let result = await this.$http.post("period.update.php", this.editperiod);
      if (result.data.status == true) {
        Swal.fire({
          icon: "success",
          title: "แก้ไขข้อมูลเรียบร้อย",
          showConfirmButton: false,
          timer: 1500
        });
        await this.periodQueryAll();
        let userSession = JSON.parse(localStorage.getItem("user")) || 0;
        this.data_syslog.ApiKey = this.ApiKey;
        this.data_syslog.user_account = userSession.user_name;
        this.data_syslog.event_log = "update";
        this.data_syslog.page_log = "period_scout";
        this.data_syslog.table_log = "period";
        this.data_syslog.date_times = this.date_today_log;
        await this.$http.post("data_syslog.insert.php", this.data_syslog);
      } else {
        Swal.fire({
          icon: "warning",
          title: "แก้ไขข้อมูลผิดพลาด",
          showConfirmButton: false,
          timer: 1500
        });
      }
      this.editperioddialog = false;
    }
  }
};
</script>

<style scoped>
.hero-header {
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
  border-radius: 16px;
  padding: 28px 32px;
}

.hero-content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.hero-icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hero-title {
  color: #fff;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 4px;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.92rem;
  margin: 0;
  max-width: 640px;
}

.edit-btn {
  background: #fff !important;
  color: #1b5e20 !important;
  border-radius: 10px;
}

.status-card {
  border-radius: 12px;
  height: 100%;
}

.status-open {
  border-color: #2e7d32 !important;
}

.status-closed {
  border-color: #ef9a9a !important;
}

.status-label {
  font-size: 0.8rem;
  color: #757575;
}

.status-value {
  font-size: 1.15rem;
  font-weight: 700;
}

.status-value-sm {
  font-size: 0.95rem;
  font-weight: 600;
}

.modern-table tbody tr:hover {
  background-color: #f5f5f5;
}

.modern-table td {
  padding: 14px !important;
}

.edit-dialog-header {
  background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
}
</style>
